import {Box, Button, Grid, Input, TextField, Typography} from '@mui/material';
import React, {useMemo, useState} from 'react';
import {guestedCount} from '../http/reportsAPI';
import {useNavigate} from 'react-router-dom';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {MaterialReactTable} from 'material-react-table';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {MRT_Localization_RU} from 'material-react-table/locales/ru';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {styled} from '@mui/material/styles';
import PhoneInput from "react-phone-number-input";
import 'react-phone-number-input/style.css'

dayjs.extend(utc);
dayjs.extend(timezone);

const TABLE_CONFIG = {
    COMMON: {
        enableStickyHeader: true,
        enableFullScreenToggle: false,
        enableDensityToggle: false,
        localization: MRT_Localization_RU,
        muiTableProps: {
            sx: {
                tableLayout: 'fixed',
            },
        },
        muiTablePaperProps: {
            sx: {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                width: '100%',
            },
        },
        muiTableContainerProps: {
            sx: {
                flex: 1,
                maxHeight: 'calc(100vh - 300px)',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                    width: '0.4em',
                    height: '0.4em',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,.1)',
                    borderRadius: '4px',
                },
            },
        },
    },
};

const TableContainer = styled(Box)(({theme}) => ({
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[24],
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    '& .MuiTableCell-body': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
}));

const Reports = () => {
    const navigate = useNavigate();
    const [inputValue, setInputValue] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [result, setResult] = useState([]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        try {
            const data = await guestedCount(inputValue.replace('+7', '8'), startDate, endDate);
            const processedData = (data.result || []).map((item) => ({
                ...item,
                createdAt: dayjs.utc(item.createdAt).local().format(),
            }));
            setResult(processedData);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    // Обработчик для преобразования 8 в +7 при вставке
    const handleChangePhone = (value) => {
        if (value && value.startsWith('8')) {
            setInputValue('+7' + value.slice(1)); // Преобразуем 8 в +7
        } else if(value && !value.startsWith('+7')) {
           setInputValue('+7' + value);
        }
        else {
            setInputValue(value);
        }
    };

    return (
        <Box sx={{
            p: 4, // Увеличиваем внутренние отступы
            maxWidth: 1600, // Ограничиваем максимальную ширину
            margin: '0 auto', // Центрируем контент
        }}>
            <Button
                variant='contained'
                color='primary'
                onClick={() => navigate('/patients')}
                sx={{mb: 3}}
            >
                Назад
            </Button>

            <Typography variant='h5' gutterBottom>
                Информация о рефералах
            </Typography>

            <form onSubmit={handleSubmit}>
                <Grid container alignItems='center' spacing={2}>
                    <Grid item xs={12} md={3}>

                        <PhoneInput
                            value={inputValue}
                            onChange={handleChangePhone}
                            placeholder={"8 (___) ___-__-__"}
                            defaultCountry="RU" // Указываем Россию как единственную страну
                            international={false} // Отключаем международный формат
                            countrySelectProps={{disabled: true}} // Блокируем выбор страны
                            countryOptions={['RU']} // Ограничиваем выбор только Россией
                            displayInitialValueAsLocalNumber={true}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            fullWidth
                            type="date"
                            label="С"
                            InputLabelProps={{shrink: true}}
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            required
                            inputProps={{
                                min: "1900-01-01",
                                max: "9999-12-31",
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            fullWidth
                            type='date'
                            label='По'
                            InputLabelProps={{shrink: true}}
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            required
                            inputProps={{
                                min: "1900-01-01",
                                max: "9999-12-31",
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Button fullWidth variant='contained' color='primary' type='submit'>
                            Запросить
                        </Button>
                    </Grid>
                </Grid>
            </form>

            <PatientReferrals data={result}/>
        </Box>
    );
};

const PatientReferrals = ({data}) => {
    const userColumns = useMemo(
        () => [
            {
                accessorKey: 'name',
                header: 'Имя',
                size: 150,
                maxSize: 200,
            },
            {
                accessorKey: 'phone',
                header: 'Телефон',
                size: 150,
                maxSize: 200,
            },
            {
                accessorKey: 'createdAt',
                header: 'Дата регистрации',
                filterVariant: 'date-range',
                size: 180,
                maxSize: 200,
                Cell: ({cell}) => {
                    const date = dayjs(cell.getValue());
                    return date.isValid() ? date.format('DD.MM.YYYY HH:mm') : 'Некорректная дата';
                },
                filterFn: (row, columnId, filterValue) => {
                    const rowDate = dayjs(row.getValue(columnId));
                    if (!rowDate.isValid()) return false;

                    const [start, end] = filterValue || [];
                    const startDate = start ? dayjs(start).startOf('day') : null;
                    const endDate = end ? dayjs(end).endOf('day') : null;

                    if (startDate && rowDate.isBefore(startDate)) return false;
                    if (endDate && rowDate.isAfter(endDate)) return false;

                    return true;
                },
            },
        ],
        []
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TableContainer>
                <Typography variant='h6' sx={{mb: 2}}>
                    Рефералы пользователя
                </Typography>

                <MaterialReactTable
                    {...TABLE_CONFIG.COMMON}
                    columns={userColumns}
                    data={data}
                    initialState={{
                        density: 'compact',
                        showColumnFilters: true,
                        columnVisibility: {_id: false},
                        sorting: [{id: 'createdAt', desc: true}],
                    }}
                    muiTableHeadCellProps={{
                        sx: {
                            fontWeight: 'bold',
                            fontSize: '0.875rem',
                            whiteSpace: 'nowrap',
                        },
                    }}
                />
            </TableContainer>
        </LocalizationProvider>
    );
};

export default Reports;
